import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, X } from 'lucide-react';

interface IPBlockedDialogProps {
  isOpen: boolean;
  errorMessage?: string;
  onClose?: () => void;
}

/**
 * Full-screen blocking dialog shown when IP validation fails
 * Can be dismissed via close button - allows user to try again later
 */
export const IPBlockedDialog: React.FC<IPBlockedDialogProps> = ({
  isOpen,
  errorMessage,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      data-testid="ip-blocked-dialog"
    >
      <div className="w-full max-w-md mx-4 bg-[#1A1A1A] rounded-2xl p-6 relative">
        {/* Close Button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors cursor-pointer"
            aria-label={t('common.close')}
            data-testid="ip-blocked-dialog-close"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-white font-bold text-xl text-center mb-4">
          {t('competition.ipBlocked.title')}
        </h2>

        {/* Message */}
        <p className="text-white/80 text-center text-sm leading-relaxed mb-6">
          {errorMessage || t('competition.ipBlocked.message')}
        </p>

        {/* Logo */}
        <div className="flex items-center justify-center pt-4 border-t border-white/10">
          <img
            src="/skypicks-logo.svg"
            alt="DraftKings SkyPicks"
            className="h-8 opacity-60"
            onError={e => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      </div>
    </div>
  );
};
